import { lazy, ComponentType } from 'react'

/**
 * Wrapper for React.lazy that handles chunk load failures gracefully.
 *
 * When a dynamic import fails (e.g., after a deployment with new chunk hashes),
 * this will automatically reload the page to fetch the latest assets.
 *
 * Uses sessionStorage to prevent infinite reload loops.
 */

const RELOAD_KEY = 'chunk_reload_attempted'
const RELOAD_EXPIRY = 10000 // 10 seconds - prevent reload loops

function shouldAttemptReload(): boolean {
  const lastReload = sessionStorage.getItem(RELOAD_KEY)
  if (!lastReload) return true

  const elapsed = Date.now() - parseInt(lastReload, 10)
  return elapsed > RELOAD_EXPIRY
}

function markReloadAttempted(): void {
  sessionStorage.setItem(RELOAD_KEY, Date.now().toString())
}

function clearReloadMarker(): void {
  sessionStorage.removeItem(RELOAD_KEY)
}

// Clear the reload marker on successful page load
if (typeof window !== 'undefined') {
  window.addEventListener('load', clearReloadMarker)
}

/**
 * Creates a lazy component with automatic page reload on chunk load failure.
 *
 * @param importFn - The dynamic import function
 * @param componentName - Optional component name for debugging
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  componentName?: string
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const component = await importFn()
      // Success - clear any reload marker
      clearReloadMarker()
      return component
    } catch (error) {
      const isChunkError =
        error instanceof Error &&
        (error.message.includes('Failed to fetch dynamically imported module') ||
         error.message.includes('Loading chunk') ||
         error.message.includes('ChunkLoadError') ||
         error.name === 'ChunkLoadError')

      if (isChunkError && shouldAttemptReload()) {
        console.warn(
          `[lazyWithRetry] Chunk load failed${componentName ? ` for ${componentName}` : ''}, reloading page...`
        )
        markReloadAttempted()
        window.location.reload()
        // Return a never-resolving promise to prevent React error during reload
        return new Promise(() => {})
      }

      // Re-throw if not a chunk error or if we already tried reloading
      throw error
    }
  })
}

/**
 * Creates a lazy component from a named export with automatic page reload on chunk load failure.
 *
 * @param importFn - The dynamic import function
 * @param exportName - The name of the export to use
 * @param componentName - Optional component name for debugging
 */
export function lazyWithRetryNamed<T extends ComponentType<any>>(
  importFn: () => Promise<Record<string, T>>,
  exportName: string,
  componentName?: string
): React.LazyExoticComponent<T> {
  return lazyWithRetry(
    () => importFn().then(m => ({ default: m[exportName] })),
    componentName || exportName
  )
}

export default lazyWithRetry
